/**
 * 🧱 Refactor BT Phase 3 — `useBtEquipeMatriceCreer` : code déplacé TEL QUEL depuis CreerBonTravailDialog.jsx (ex-L204-438).
 * `ctx` = noms du composant lus par ce code (mêmes valeurs de rendu qu'avant) ; retourne ce qu'il déclare.
 */
import { API_URL, calculerCoutEquipe, calculerHeuresBudgetees } from '../components/crm/bon-travail/utils';
import axios from 'axios';
import { toast } from 'sonner';

export function useBtEquipeMatriceCreer(ctx) {
  const {
    dossier,
    employees,
    form,
    heureDebutDirty,
    setCongesEquipe,
    setEquipeOccupee,
    setForm,
    setShowAddEmploye,
  } = ctx;

  const handleChefChange = (empId) => {
    // 🐛 [Phase v13.46 — 2026-07-14] Sentinelle « __none__ » = mode « — Sans chef — » :
    //   retire le chef mais PRÉSERVE les employés non-chef déjà ajoutés manuellement
    //   (cohérence avec EditerBonTravailDialog qui garde `membresExtras` en mode Sans chef).
    if (empId === '__none__') {
      setForm(f => ({
        ...f,
        chef_equipe_id: '',
        chef_equipe_nom: '',
        employes_assignes: (f.employes_assignes || []).filter(e => !e.est_chef),
      }));
      setEquipeOccupee({});
      return;
    }
    const emp = employees.find(e => e.id === empId);
    const chefNom = emp ? `${emp.prenom} ${emp.nom}` : '';
    setForm(f => {
      // 🚀 [Phase 107.B — 2026-07-14] Fin des équipes prédéterminées :
      //   sélectionner un chef n'auto-ajoute PLUS ses peintres d'équipe.
      //   L'utilisatrice ajoute manuellement chaque employé pour chaque jour.
      const newAssignes = [
        { employee_id: empId, employee_nom: chefNom, dates_assignees: [...f.dates_chantier], est_chef: true },
      ];

      // Auto-calculer heures budgétées
      const montant = parseFloat(dossier?.montant_soumission || dossier?.montant_final || 0) || (() => {
        const soumAcceptee = (dossier?.soumissions || []).find(s => s.statut === 'acceptee');
        return parseFloat(soumAcceptee?.montant_final || soumAcceptee?.montant_total || 0);
      })();
      // Nouveau calcul : moyenne pondérée tous les terrain (fait par calculerCoutEquipe refactorisé)
      const equipe = calculerCoutEquipe(empId, employees, f.dates_chantier?.[0]);
      let newHeures = f.heures_budgetees;
      if (montant > 0 && equipe.coutMoyen > 0) {
        const calc = calculerHeuresBudgetees(montant, equipe.coutMoyen, equipe.nbMembres, f.dates_chantier);
        newHeures = calc.heures.toString();
      }

      // 🆕 Phase C4 — Pré-remplir l'heure de début depuis l'horaire du chef.
      // Priorité : horaire_travail (champ dédié chef d'équipe) AVANT horaire_admin
      // (ce dernier a un défaut historique 08:00 même pour les non-admin → on l'évite).
      // Dirty flag : si l'utilisateur a déjà saisi manuellement une heure, on respecte son choix.
      const chefHeureDebut =
        emp?.horaire_travail?.defaut?.heure_debut ||
        emp?.horaire_admin?.defaut?.heure_debut ||
        '';
      const heureDebutFinale = heureDebutDirty
        ? (f.heure_debut || chefHeureDebut)
        : (chefHeureDebut || f.heure_debut || '');

      return {
        ...f,
        chef_equipe_id: empId,
        chef_equipe_nom: chefNom,
        employes_assignes: newAssignes,
        heures_budgetees: newHeures,
        heure_debut: heureDebutFinale,
      };
    });
    
    // ✅ FIX: Mettre à jour le chef d'équipe dans le dossier client
    if (dossier?.id && empId) {
      axios.put(`${API_URL}/api/crm/dossiers/${dossier.id}`, { ...dossier, chef_equipe_id: empId, chef_equipe_nom: chefNom }).catch(() => {});
    }

    // Charger les occupations de l'équipe et les congés
    if (empId) {
      axios.get(`${API_URL}/api/crm/equipe-disponibilite/${empId}`).then(r => r.data)
        .then(data => setEquipeOccupee(data.dates_occupees || {}))
        .catch(() => setEquipeOccupee({}));
      axios.get(`${API_URL}/api/conges/calendrier`).then(r => r.data)
        .then(data => {
          // 🧱 Phase 4 — Lot E : congés du chef et des employés déjà sur le BT (plus d'équipe fixe).
          const equipeIds = [empId, ...(form.employes_assignes || []).map(e => e.employee_id)];
          const congesMap = {};
          for (const c of (data.conges || [])) {
            if (equipeIds.includes(c.employee_id) && c.date_debut) {
              const start = new Date(c.date_debut + 'T12:00:00');
              const end = new Date((c.date_fin || c.date_debut) + 'T12:00:00');
              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const ds = d.toISOString().split('T')[0];
                congesMap[ds] = c.employee_nom || 'Congé';
              }
            }
          }
          setCongesEquipe(congesMap);
        })
        .catch(() => setCongesEquipe({}));
    }

  };

  const recalculerHeures = (assignes, datesCh) => {
    const montant = parseFloat(dossier?.montant_soumission || dossier?.montant_final || 0) || (() => {
      const soumAcceptee = (dossier?.soumissions || []).find(s => s.statut === 'acceptee');
      return parseFloat(soumAcceptee?.montant_final || soumAcceptee?.montant_total || 0);
    })();
    if (montant <= 0 || assignes.length === 0) return null;
    // Calculer le coût moyen des employés réellement assignés
    let coutTotal = 0;
    for (const a of assignes) {
      const emp = employees.find(e => e.id === a.employee_id);
      const cout = parseFloat(emp?.salaires?.cout_employeur || 0);
      coutTotal += cout;
    }
    const coutMoyen = coutTotal / assignes.length;
    if (coutMoyen <= 0) return null;
    const calc = calculerHeuresBudgetees(montant, coutMoyen, assignes.length, datesCh);
    return calc.heures > 0 ? calc.heures.toString() : null;
  };

  const addEmployeAssigne = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    // 🧱 Phase 4 — Lot E (B-27) : garde-fou « déjà dans la liste » (chef ou membre), message discret
    if (empId === form?.chef_equipe_id || (form?.employes_assignes || []).some(e => e.employee_id === empId)) {
      toast.info(`${emp.prenom} ${emp.nom} est déjà dans la liste`, { id: 'msg-employe-deja-present' });
      setShowAddEmploye(false);
      return;
    }
    setForm(f => {
      if (f.employes_assignes.find(e => e.employee_id === empId)) return f;
      const newAssignes = [...f.employes_assignes, {
        employee_id: empId,
        employee_nom: `${emp.prenom} ${emp.nom}`,
        dates_assignees: [...f.dates_chantier],
        est_chef: false
      }];
      const newHeures = recalculerHeures(newAssignes, f.dates_chantier);
      return { ...f, employes_assignes: newAssignes, ...(newHeures ? { heures_budgetees: newHeures } : {}) };
    });
    setShowAddEmploye(false);
  };

  const removeEmployeAssigne = (empId) => {
    setForm(f => {
      const newAssignes = f.employes_assignes.filter(e => e.employee_id !== empId);
      const newHeures = recalculerHeures(newAssignes, f.dates_chantier);
      return { ...f, employes_assignes: newAssignes, ...(newHeures ? { heures_budgetees: newHeures } : {}) };
    });
  };

  const toggleDate = (dateStr) => {
    setForm(f => {
      const has = f.dates_chantier.includes(dateStr);
      const newDates = has
        ? f.dates_chantier.filter(d => d !== dateStr)
        : [...f.dates_chantier, dateStr].sort();
      // 🆕 2026-04-30 : retirer la date des dates_assignees de chaque employé si elle est désélectionnée
      const newAssignes = f.employes_assignes.map(e => ({
        ...e,
        dates_assignees: has
          ? (e.dates_assignees || []).filter(d => d !== dateStr)
          : [...(e.dates_assignees || []), dateStr].filter((v, i, a) => a.indexOf(v) === i).sort(),
      }));
      const newChefDates = has
        ? (f.chef_dates_assignees || []).filter(d => d !== dateStr)
        : [...(f.chef_dates_assignees || []), dateStr].filter((v, i, a) => a.indexOf(v) === i).sort();
      return { ...f, dates_chantier: newDates, employes_assignes: newAssignes, chef_dates_assignees: newChefDates };
    });
  };

  // 🆕 2026-04-30 : bascule la présence d'un employé pour UN jour donné
  const toggleEmployeJour = (empId, dateStr, isChef) => {
    setForm(f => {
      if (isChef) {
        const cur = f.chef_dates_assignees || [...(f.dates_chantier || [])];
        const newChef = cur.includes(dateStr)
          ? cur.filter(d => d !== dateStr)
          : [...cur, dateStr].sort();
        return { ...f, chef_dates_assignees: newChef };
      }
      const newAssignes = f.employes_assignes.map(e => {
        if (e.employee_id !== empId) return e;
        const cur = e.dates_assignees || [...(f.dates_chantier || [])];
        const newDates = cur.includes(dateStr)
          ? cur.filter(d => d !== dateStr)
          : [...cur, dateStr].sort();
        return { ...e, dates_assignees: newDates };
      });
      return { ...f, employes_assignes: newAssignes };
    });
  };

  // 🆕 2026-05 : sélectionne le chef d'équipe pour UN jour précis (chefs_par_jour).
  // Auto-ajoute le chef sélectionné à employes_assignes pour ce jour si absent,
  // et ajoute aussi ses peintres terrain si pas déjà présents (auto-fill équipe).
  const setChefDuJour = (dateStr, empId) => {
    setForm(f => {
      const newChefs = { ...(f.chefs_par_jour || {}) };
      if (!empId) {
        delete newChefs[dateStr];
        return { ...f, chefs_par_jour: newChefs };
      }
      newChefs[dateStr] = empId;
      // Auto-ajout du chef dans employes_assignes pour ce jour
      let newAssignes = [...f.employes_assignes];
      const chefEmp = employees.find(e => e.id === empId);
      if (chefEmp) {
        const chefIdx = newAssignes.findIndex(e => e.employee_id === empId);
        if (chefIdx === -1) {
          newAssignes.push({
            employee_id: empId,
            employee_nom: `${chefEmp.prenom} ${chefEmp.nom}`,
            dates_assignees: [dateStr],
            est_chef: true,
          });
        } else {
          const cur = newAssignes[chefIdx].dates_assignees || [];
          if (!cur.includes(dateStr)) {
            newAssignes[chefIdx] = {
              ...newAssignes[chefIdx],
              dates_assignees: [...cur, dateStr].sort(),
            };
          }
        }
        // 🧱 Phase 4 — Lot E (équipe fixe abolie) : plus d'auto-remplissage des peintres de la fiche du chef.
      }
      return { ...f, chefs_par_jour: newChefs, employes_assignes: newAssignes };
    });
  };


  return { addEmployeAssigne, handleChefChange, recalculerHeures, removeEmployeAssigne, setChefDuJour, toggleDate, toggleEmployeJour };
}
